import {
  Box,
  Text,
  Image,
  Stack,
  ListItem,
  ListIcon,
  List,
  Link,
  IconButton
} from '@chakra-ui/core'
import moment from 'moment'
import React from 'react'
import { VscCheck } from 'react-icons/vsc'
import {
  EvidenceCustomDataFieldsDynamicZone,
  Maybe,
  UploadFileEntity
} from '../../../../generated/graphql'
import { theme } from '../../../../theme'
import { Alert } from '../../../../components'
import { Rating } from 'react-simple-star-rating'
import { getImageByFileExtension } from '../../../../utils'

type DataFieldsDetailsGeneratorProps = {
  dataFields: Maybe<Maybe<EvidenceCustomDataFieldsDynamicZone>[]> | undefined
}

const DataFieldsDetailsGenerator: React.FC<DataFieldsDetailsGeneratorProps> = ({ dataFields }) => {
  return (
    <Stack fontSize="xs" spacing={8}>
      {dataFields?.map((field, index) => {
        if (!field) return null

        switch (field.__typename) {
          case 'ComponentCustomDataFieldsText':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success" withIcon>
                  <Text>{field?.text_value}</Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsCheckbox':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <List>
                    {field?.option?.map((value, idx) => (
                      <ListItem key={idx} ml={4}>
                        <ListIcon icon={VscCheck} color="green.500" />
                        {value?.option}
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsDataList':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <List>
                    {field?.option?.map((item, idx) => (
                      <ListItem key={idx} ml={4}>
                        <ListIcon icon={VscCheck} color="green.500" />
                        {item?.option}
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsDateTimeField':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>{' '}
                <Alert status="success">
                  <Text fontStyle="italic" color={theme.colors.gray[600]} fontSize="sm">
                    {moment(field?.datetime).format('YYYY-MM-DD HH:mm:ss')}
                  </Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsEmailField':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>{' '}
                <Alert status="success">
                  <Text>{field?.email}</Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsImageField':
            const images = field?.images?.data || ([] as UploadFileEntity[])

            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Stack direction="row" flexWrap="wrap" my={2}>
                  {images.map((image) =>
                    image.attributes?.mime?.includes('image/') ? (
                      <Image
                        key={image.id}
                        src={
                          image.attributes?.formats?.thumbnail?.url || image.attributes?.url || ''
                        }
                        alt={image.attributes?.name || 'Image'}
                        w={24}
                        h={24}
                        objectFit="cover"
                        rounded="lg"
                        mr={2}
                        mb={2}
                      />
                    ) : (
                      <Stack alignItems="center" key={image.id}>
                        <Image
                          key={image.id}
                          src={getImageByFileExtension(image.attributes?.name || '')}
                          alt={image.attributes?.name || 'Image'}
                          w={16}
                          h={16}
                          objectFit="contain"
                          rounded="lg"
                          mr={2}
                          mb={2}
                        />
                        <Text fontSize="x-small" textAlign="center">
                          {image.attributes?.name || 'File'}
                        </Text>
                        <Link
                          href={image.attributes?.url || ''}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={image.attributes?.name || ''}
                        >
                          <IconButton
                            aria-label="Download File"
                            icon="download"
                            size="sm"
                            variantColor="green"
                          />
                        </Link>
                      </Stack>
                    )
                  )}
                </Stack>
              </Box>
            )

          case 'ComponentCustomDataFieldsMetricsSpecific':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <Text>
                    {field?.number_value}{' '}
                    <Text as="span" fontStyle="italic">
                      {field?.metric}
                    </Text>
                  </Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsNumberDropDown':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">{field?.value}</Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsNumberField':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <Text>{field?.number_field_value}</Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsNumberRange':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <Text>
                    {field?.min} - {field?.max}
                  </Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsPercentage':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <Text>{field?.value}%</Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsPhoneNumberField':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <Text>{field?.phoneNumber}</Text>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsRadio':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <List>
                    {field?.option?.map((value, idx) => (
                      <ListItem key={idx} ml={4}>
                        <ListIcon icon={VscCheck} color="green.500" />
                        {value?.option}
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              </Box>
            )
          case 'ComponentCustomDataFieldsRating':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Box display="flex" alignItems="center">
                  <Rating
                    initialValue={field.rating ? field.rating : 0}
                    readonly
                    SVGstyle={{ display: 'inline' }}
                    size={20}
                  />
                </Box>
              </Box>
            )
          case 'ComponentCustomDataFieldsSelect':
            return (
              <Box key={index} mb={2}>
                <Text fontWeight="bold">{field?.label}</Text>
                <Alert status="success">
                  <List>
                    {field?.option?.map((value, idx) => (
                      <ListItem key={idx} ml={4}>
                        <ListIcon icon={VscCheck} color="green.500" />
                        {value?.option}
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              </Box>
            )

          default:
            return (
              <Box key={index} mb={2} flexWrap="wrap">
                <Text fontWeight="bold">Unknown Field Type</Text>
                <Text>{JSON.stringify(field)}</Text>
              </Box>
            )
        }
      }) || (
        <Box p={4}>
          <Text>No data fields available.</Text>
        </Box>
      )}
    </Stack>
  )
}

export default DataFieldsDetailsGenerator
